
import log from "plugins/loglevel/index"

/*
# This operation converts coordinates value (latitude and longitude) in
# Standard WGS84 to BD09 (used by Baidu Maps)
#
# References:
# - https://en.wikipedia.org/wiki/World_Geodetic_System
# - https://en.wikipedia.org/wiki/Restrictions_on_geographic_data_in_China
*/


const MATH_A = 6378245.0
const MATH_EE = 0.00669342162296594323


class Operation {
  private lat: number
  private lng: number

  constructor({latitude, longitude}: {latitude: number | string, longitude: number | string}){
    this.lat = parseFloat(latitude.toString())
    this.lng = parseFloat(longitude.toString())
  }
  public call(){
    this.wgs84_to_gsj02()

    return {
      latitude:  this.lat,
      longitude: this.lng,
    }
  }

  // Private

  // as according to the above link, do not convert if coordinates are outside the range of China
  private is_in_china(){
    return (this.lng > 72.004) && (this.lng < 137.8347) && (this.lat > 0.8293) && (this.lat < 55.8271)
  }

  // First convert from WGS84 (World Geodetic System) to GSJ02 (Mars Geodetic System)
  private wgs84_to_gsj02(){

    let dLat = transform_latitude(this.lng - 105.0, this.lat - 35.0)
    let dLon = transform_longitude(this.lng - 105.0, this.lat - 35.0)
    const radLat = this.lat / 180.0 * Math.PI

    let magic = Math.sin(radLat)
    magic = 1 - MATH_EE * magic * magic
    const sqrtMagic = Math.sqrt(magic)

    dLat = (dLat * 180.0) / ((MATH_A * (1 - MATH_EE)) / (magic * sqrtMagic) * Math.PI)
    dLon = (dLon * 180.0) / (MATH_A / sqrtMagic * Math.cos(radLat) * Math.PI)

    this.lat = this.lat + dLat
    this.lng = this.lng + dLon
  }

  // Then convert from GSJ02 (Mars Geodetic System) to BD09 (Baidu Coordinate System)
  private gsj02_to_bd09(){
    const x = this.lng
    const y = this.lat
    const z = Math.sqrt(x * x + y * y) + 0.00002 * Math.sin(y * Math.PI * 3000.0 / 180.0)
    const theta = Math.atan2(y, x) + 0.000003 * Math.cos(x * Math.PI * 3000.0 / 180.0)
    this.lng = z * Math.cos(theta) + 0.0065
    this.lat = z * Math.sin(theta) + 0.006
  }
}

function transform_latitude(lon: number, lat: number){
  log.debug(">>> transform_latitude >>>")
  let ret = -100.0 + 2.0 * lon + 3.0 * lat + 0.2 * lat * lat + 0.1 * lon * lat + 0.2 * Math.sqrt(Math.abs(lon))
  ret = ret + (20.0 * Math.sin(6.0 * lon * Math.PI) + 20.0 * Math.sin(2.0 * lon * Math.PI)) * 2.0 / 3.0
  ret = ret + (20.0 * Math.sin(lat * Math.PI) + 40.0 * Math.sin(lat / 3.0 * Math.PI)) * 2.0 / 3.0
  ret = ret + (160.0 * Math.sin(lat / 12.0 * Math.PI) + 320 * Math.sin(lat * Math.PI / 30.0)) * 2.0 / 3.0
  return ret
}

function transform_longitude(lon: number, lat: number){
  let ret = 300.0 + lon + 2.0 * lat + 0.1 * lon * lon + 0.1 * lon * lat + 0.1 * Math.sqrt(Math.abs(lon))
  ret = ret + (20.0 * Math.sin(6.0 * lon * Math.PI) + 20.0 * Math.sin(2.0 * lon * Math.PI)) * 2.0 / 3.0
  ret = ret + (20.0 * Math.sin(lon * Math.PI) + 40.0 * Math.sin(lon / 3.0 * Math.PI)) * 2.0 / 3.0
  ret = ret + (150.0 * Math.sin(lon / 12.0 * Math.PI) + 300.0 * Math.sin(lon / 30.0 * Math.PI)) * 2.0 / 3.0
  return ret
}

export default Operation
